import classNames from 'classnames'
import {gsap} from 'gsap'
import {DrawSVGPlugin} from 'gsap/DrawSVGPlugin'
import * as React from 'react'
import {render} from 'storyblok-rich-text-react-renderer'

import {isEmptyRichtext, unwrapAllNodeResolvers} from '@/common/utils'

gsap.registerPlugin(DrawSVGPlugin)

const themes = {
  light: 'text-white',
  dark: 'text-purple-900',
  yellow: 'text-yellow-400',
}

const shapeThemes = {
  light: 'stroke-yellow-400',
  dark: 'stroke-violet-600',
  yellow: 'stroke-turquoise-400',
}

const highlightThemes = {
  light: 'bg-yellow-400 text-violet-400',
  dark: 'bg-violet-600 text-yellow-300',
  yellow: 'bg-turquoise-600 text-yellow-200',
}

const coloredTextThemes = {
  light: 'text-white',
  dark: 'text-violet-600',
  yellow: 'text-turquoise-400',
}

const Heading = ({heading, size = 'm', theme = 'dark', className}) => {
  const containerRef = React.useRef()

  React.useEffect(() => {
    const container = containerRef.current
    if (container && container.length) {
      const context = gsap.context(() => {
        gsap.fromTo(
          'path',
          {drawSVG: '0%'},
          {
            drawSVG: '100%',
            scrollTrigger: {
              trigger: container,
              start: 'center bottom',
            },
          }
        )
      }, container)

      return () => context.revert()
    }
  }, [])

  return isEmptyRichtext(heading) ? null : (
    <span
      ref={containerRef}
      className={classNames(
        'block',
        {'heading-xl-satoshi [&>b]:heading-xl-millik': size === 'xl'},
        {'heading-l-satoshi [&>b]:heading-l-millik': size === 'l'},
        {'heading-m-satoshi [&>b]:heading-m-millik': size === 'm'},
        {'heading-s-satoshi [&>b]:heading-s-millik': size === 's'},
        {'heading-xs-satoshi [&>b]:heading-xs-millik': size === 'xs'},
        themes[theme],
        className
      )}
    >
      {render(heading, {
        nodeResolvers: unwrapAllNodeResolvers,
        markResolvers: {
          underline: (children) => (
            <span className='relative inline-block'>
              {children}
              <Underline className={classNames('absolute top-full w-full', shapeThemes[theme])} />
            </span>
          ),
          italic: (children) => (
            <span className='relative inline-block'>
              {children}
              <UnderlineRough
                className={classNames('absolute top-full w-full', shapeThemes[theme])}
              />
            </span>
          ),
          strike: (children) => (
            <span className='relative inline-block'>
              {children}
              <Circle
                className={classNames(
                  'absolute -left-[10%] -top-[30%] w-[120%]',
                  shapeThemes[theme]
                )}
              />
            </span>
          ),
          highlight: (children) => (
            <span
              className={classNames(
                'relative inline-block -rotate-[4deg] rounded-2xl px-6 py-3',
                highlightThemes[theme]
              )}
            >
              {children}
            </span>
          ),
          textStyle: (children) => <span className={coloredTextThemes[theme]}>{children}</span>,
        },
      })}
    </span>
  )
}

const Underline = (props) => (
  <svg viewBox='0 0 304 15' fill='none' xmlns='http://www.w3.org/2000/svg' {...props}>
    <path
      d='M0.105671 13.9293C38.339 -2.04535 259.409 -3.16352 304 11.1253'
      strokeWidth='2'
      strokeLinecap='round'
      vectorEffect='non-scaling-stroke'
    />
  </svg>
)

const UnderlineRough = (props) => (
  <svg viewBox='0 0 229 38' fill='none' xmlns='http://www.w3.org/2000/svg' {...props}>
    <path
      d='M1.51152 2.62025C55.3862 2.34241 109.019 1.66441 163.037 1.61993C183.831 1.6028 204.66 1.80162 225.659 2.30378C232.538 2.46827 222.031 2.91865 220.457 3.07366C196.123 5.46959 169.57 6.88505 144.2 8.58954C138.114 8.99843 1.77175 18.1388 26.3295 18.009C65.2549 17.8031 168.169 16.9371 207.424 17.3303C248.085 15.1997 153.825 31.0731 136.377 36.0182'
      strokeWidth='2'
      strokeLinecap='round'
      vectorEffect='non-scaling-stroke'
    />
  </svg>
)

const Circle = (props) => (
  <svg viewBox='0 0 345 118' fill='none' xmlns='http://www.w3.org/2000/svg' {...props}>
    <path
      d='M183.004 12.0352C247.389 6.2158 427.357 50.5175 298.804 93.0009C32.1263 181.13 -180.46 -11.4638 239.16 1.83553'
      strokeWidth='2'
      strokeLinecap='round'
      vectorEffect='non-scaling-stroke'
    />
  </svg>
)

export default Heading
